/* <Sentence> — renders compiled sentence parts with the DATUM styling of
 * the v13 cards (RkClaimsCard: the figure inside a claim line is a <b>
 * inside `.txt`). Same element, same class canon: a datum is a <b> with
 * `sn-datum`, highlighted and slightly larger, so the meaning is
 * identical everywhere — on a card it is what approving writes, on a
 * read surface it is what was written.
 *
 * Accessible by construction: the datum is plain text inside the
 * sentence, never colour alone; a missing value is said in words. */

import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import DocumentLink from './documents/DocumentLink'
import { readerContextFromHref, type DocumentRef, type ReaderContext } from '../lib/documentReader'
import {
  compileSentence,
  tightAfter,
  tightBefore,
  type CompileOptions,
  type SentencePart,
  type SlotValue,
} from '../lib/sentences/compile'
import './sentence.css'

/** One part of a sentence — a datum, a missing-value phrase, or words.
 *  `tight`: the next part opens with punctuation, so the datum's box
 *  pulls the comma or full stop back against the value ("N22.pdf,");
 *  `tightLeft`: the part before ends with an opening quote or bracket. */
export function Part({
  p,
  tight,
  tightLeft,
}: {
  p: SentencePart
  tight?: boolean
  tightLeft?: boolean
}) {
  if (p.kind === 'datum')
    return (
      <b
        className={`sn-datum${tight ? ' tight' : ''}${tightLeft ? ' tight-l' : ''}`}
        data-slot={p.slot}
        data-kind={p.datumKind}
      >
        {p.text}
      </b>
    )
  if (p.kind === 'missing')
    return (
      <span className="sn-missing" data-slot={p.slot}>
        {p.text}
      </span>
    )
  return <span>{p.text}</span>
}

/** A sentence whose ONE named datum is a door: the compiled parts, with
 *  the `slot` datum rendered as a Link (the drawer's proven lines — the
 *  paper is the door to the document page). */
export function LinkedSentence({
  meaning,
  values,
  slot,
  href,
  linkTestId,
  documentRef,
  documentRefs,
  documentContext,
  documentLimit,
  className,
  children,
  'data-testid': testId,
}: {
  meaning: string
  values?: Record<string, SlotValue>
  slot: string
  href: string | null
  linkTestId?: string
  documentRef?: DocumentRef | null
  documentRefs?: DocumentRef[]
  documentContext?: ReaderContext
  documentLimit?: number
  className?: string
  children?: React.ReactNode
  'data-testid'?: string
}) {
  const compiled = compileSentence(meaning, values ?? {})
  const parts = compiled.parts
  return (
    <span
      className={`sn${className ? ` ${className}` : ''}`}
      data-meaning={meaning}
      data-source={compiled.source}
      title={compiled.text}
      data-testid={testId}
    >
      {parts.map((p, i) =>
        p.kind === 'datum' && p.slot === slot && documentRefs?.length ? (
          <span key={i} className="sn-datum" data-slot={p.slot}>
            {documentRefs.slice(0, documentLimit).map((ref, index) => (
              <Fragment key={ref.document_id}>
                {index > 0 ? ', ' : ''}
                <DocumentLink document={ref} context={documentContext} />
              </Fragment>
            ))}
            {documentLimit != null && documentRefs.length > documentLimit
              ? ` and ${documentRefs.length - documentLimit} more`
              : null}
          </span>
        ) : p.kind === 'datum' && p.slot === slot && href && documentRef ? (
          <DocumentLink
            key={i}
            document={documentRef}
            context={readerContextFromHref(href)}
            className={`sn-datum drawer-evlink${tightBefore(parts, i) ? ' tight' : ''}`}
            data-slot={p.slot}
            data-kind={p.datumKind}
            data-testid={linkTestId}
          />
        ) : p.kind === 'datum' && p.slot === slot && href ? (
          <Link
            key={i}
            to={href}
            className={`sn-datum drawer-evlink${tightBefore(parts, i) ? ' tight' : ''}`}
            data-slot={p.slot}
            data-kind={p.datumKind}
            data-testid={linkTestId}
          >
            {p.text}
          </Link>
        ) : (
          <Part p={p} key={i} tight={tightBefore(parts, i)} tightLeft={tightAfter(parts, i)} />
        )
      )}
      {children}
    </span>
  )
}

export function SentenceParts({ parts }: { parts: SentencePart[] }) {
  return (
    <>
      {parts.map((p, i) => (
        <Part p={p} key={i} tight={tightBefore(parts, i)} tightLeft={tightAfter(parts, i)} />
      ))}
    </>
  )
}

export interface SentenceProps extends CompileOptions {
  meaning: string
  values?: Record<string, SlotValue>
  className?: string
  /** Rendered element — a span by default so it sits inside any row. */
  as?: 'span' | 'p' | 'div'
  style?: React.CSSProperties
  'data-testid'?: string
  /** Words rendered after the sentence, inside the same element (a small
   *  policy code, a door) — never part of the compiled text. */
  children?: React.ReactNode
}

export default function Sentence({
  meaning,
  values,
  className,
  as: Tag = 'span',
  fallback,
  phrasebook,
  children,
  ...rest
}: SentenceProps) {
  const compiled = compileSentence(meaning, values ?? {}, { fallback, phrasebook })
  return (
    <Tag
      className={`sn${className ? ` ${className}` : ''}`}
      data-meaning={meaning}
      data-source={compiled.source}
      title={compiled.text}
      {...rest}
    >
      <SentenceParts parts={compiled.parts} />
      {children}
    </Tag>
  )
}
